import type { CampaignEntry, CampaignsState } from './types';

import { createSlice } from '@reduxjs/toolkit';

import {
  fetchAllDynamicCampaign,
  fetchCampaignLeaderboard,
  fetchDynamicCampaign,
  fetchUserData,
} from './thunks';

const createDefaultCampaignEntry = (): CampaignEntry => ({
  userData: { data: null, error: null, loading: false },
  leaderboard: { data: null, error: null, loading: false, totalPages: null },
  dynamicData: { data: null, error: null, loading: false },
});

export const campaignSliceInitialState: CampaignsState = {
  entries: {},
  campaigns: {
    data: null,
    error: null,
    loading: false,
  },
};

export const campaignsSlice = createSlice({
  name: 'campaign',
  initialState: campaignSliceInitialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllDynamicCampaign.pending, (state) => {
        state.campaigns.data = null;
        state.campaigns.loading = true;
        state.campaigns.error = null;
      })
      .addCase(fetchAllDynamicCampaign.fulfilled, (state, action) => {
        state.campaigns.data = action.payload;
        state.campaigns.loading = false;
        state.campaigns.error = null;
      })
      .addCase(fetchAllDynamicCampaign.rejected, (state, action) => {
        state.campaigns.data = null;
        state.campaigns.loading = false;
        state.campaigns.error = action.payload ?? null;
      })
      .addCase(fetchUserData.pending, (state, action) => {
        const id = action.meta.arg.id;
        if (!state.entries[id]) {
          state.entries[id] = createDefaultCampaignEntry();
        }
        state.entries[id].userData.data = null;
        state.entries[id].userData.loading = true;
        state.entries[id].userData.error = null;
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        const id = action.meta.arg.id;
        if (!state.entries[id]) {
          state.entries[id] = createDefaultCampaignEntry();
        }
        state.entries[id].userData.data = action.payload;
        state.entries[id].userData.loading = false;
        state.entries[id].userData.error = null;
      })
      .addCase(fetchUserData.rejected, (state, action) => {
        const id = action.meta.arg.id;
        if (!state.entries[id]) {
          state.entries[id] = createDefaultCampaignEntry();
        }
        state.entries[id].userData.data = null;
        state.entries[id].userData.loading = false;
        state.entries[id].userData.error = action.payload ?? null;
      })
      .addCase(fetchCampaignLeaderboard.pending, (state, action) => {
        const id = action.meta.arg.id;
        if (!state.entries[id]) {
          state.entries[id] = createDefaultCampaignEntry();
        }
        state.entries[id].leaderboard.data = null;
        state.entries[id].leaderboard.loading = true;
        state.entries[id].leaderboard.error = null;
      })
      .addCase(fetchCampaignLeaderboard.fulfilled, (state, action) => {
        const id = action.meta.arg.id;
        if (!state.entries[id]) {
          state.entries[id] = createDefaultCampaignEntry();
        }
        state.entries[id].leaderboard.data = action.payload.leaderboard;
        state.entries[id].leaderboard.totalPages = action.payload.totalPages;
        state.entries[id].leaderboard.loading = false;
        state.entries[id].leaderboard.error = null;
      })
      .addCase(fetchCampaignLeaderboard.rejected, (state, action) => {
        const id = action.meta.arg.id;
        if (!state.entries[id]) {
          state.entries[id] = createDefaultCampaignEntry();
        }
        state.entries[id].leaderboard.data = null;
        state.entries[id].leaderboard.loading = false;
        state.entries[id].leaderboard.error = action.payload ?? null;
      })
      .addCase(fetchDynamicCampaign.pending, (state, action) => {
        const id = action.meta.arg.id;
        if (!state.entries[id]) {
          state.entries[id] = createDefaultCampaignEntry();
        }
        state.entries[id].dynamicData.data = null;
        state.entries[id].dynamicData.loading = true;
        state.entries[id].dynamicData.error = null;
      })
      .addCase(fetchDynamicCampaign.fulfilled, (state, action) => {
        const id = action.meta.arg.id;
        if (!state.entries[id]) {
          state.entries[id] = createDefaultCampaignEntry();
        }
        state.entries[id].dynamicData.data = action.payload;
        state.entries[id].dynamicData.loading = false;
        state.entries[id].dynamicData.error = null;
      })
      .addCase(fetchDynamicCampaign.rejected, (state, action) => {
        const id = action.meta.arg.id;
        if (!state.entries[id]) {
          state.entries[id] = createDefaultCampaignEntry();
        }
        state.entries[id].dynamicData.data = null;
        state.entries[id].dynamicData.loading = false;
        state.entries[id].dynamicData.error = action.payload ?? null;
      });
  },
});

export const CampaignsReducer = campaignsSlice.reducer;
